import { FC } from 'react';
import { Button } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

import LoadingIndicator from '../LoadingIndicator/LoadingIndicator';
import {
  borderColor,
  FTMuiButtonVariantMap,
  FTVariantMuiColorMap,
  getFTVariantLoadingIconColorMap,
  hoverBackground,
} from './helpers';
import { ButtonProps } from './types';

const ElementsButton: FC<React.PropsWithChildren<ButtonProps>> = ({
  children,
  disabled = false,
  fullWidth = false,
  hoverBackgroundColor,
  isLoading = false,
  onClick,
  size = 'medium',
  startIcon,
  variant = 'main',
  ...otherProps
}) => {
  const theme = useTheme();

  const isDarkMode = theme.palette.mode === 'dark';
  const muiVariant = FTMuiButtonVariantMap[variant];
  const color = FTVariantMuiColorMap[variant];
  const loadingDotColor = getFTVariantLoadingIconColorMap(theme)[variant];

  return (
    <Button
      {...otherProps}
      disabled={disabled}
      variant={muiVariant}
      color={color}
      onClick={onClick}
      fullWidth={fullWidth}
      startIcon={!isLoading && startIcon}
      sx={[
        {
          '& .MuiButton-startIcon': Boolean(children)
            ? {
                marginRight: '4px',
              }
            : {
                margin: 0,
              },
          '& .MuiSvgIcon-root': {
            height: '.8em',
            width: '.8em',
          },
          '&:active, &:focus': {
            border: `2px solid ${borderColor(variant, theme)}`,
          },
          '&:hover': {
            backgroundColor:
              hoverBackgroundColor ?? hoverBackground(variant, theme),
          },
          border: '2px solid transparent',
          fontSize: '14px',
          height: size === 'large' ? '40px' : '32px',
          lineHeight: 1,
          minWidth: 'initial',
          typography: size === 'large' ? 'font14Bold' : 'font14Medium',
          whiteSpace: 'nowrap',
        },
        isDarkMode && {
          '&:disabled': {
            background: theme.palette.colors.grey[800],
            color: theme.palette.colors.grey[500],
          },
        },

        Boolean(children)
          ? {
              padding: size === 'large' ? '10px 24px' : '6px 16px',
            }
          : {
              padding: 0,
              width: size === 'large' ? '40px' : '32px',
            },
      ]}
    >
      <ChildrenContainer isLoading={isLoading}>{children}</ChildrenContainer>
      {isLoading && (
        <AbsoluteLoadingIndicator
          color={loadingDotColor}
          size={Boolean(children) ? 'large' : size}
        />
      )}
    </Button>
  );
};

const ChildrenContainer = styled('div')<{ isLoading: boolean }>`
  align-items: center;
  display: inline-flex;
  justify-content: center;
  ${props => props.isLoading && 'visibility: hidden;'}
`;

const AbsoluteLoadingIndicator = styled(LoadingIndicator)`
  position: absolute;
`;

export default ElementsButton;
